// Utilitários de cálculo baseados em planos dinâmicos vindos da configuração.
// Regras (Fev/2026):
//   - Plano contém: name, price (valor de venda), lucro_liquido (unitário)
//   - Comissão é personalizada POR VENDEDOR (0..1) — vem de users[].commission_pct
//   - Métricas agregadas por vendedor:
//       receita         = soma dos preços de venda
//       lucro_bruto     = soma dos lucros unitários dos planos (sem descontar comissão)
//       comissao        = receita * commission_pct do vendedor
//       lucro_final     = lucro_bruto - comissao_total (base + bônus)
export const DEFAULT_PLANS = [
  { name: "Básico", price: 79.90, lucro_liquido: 32.0 },
  { name: "Intermediário", price: 129.90, lucro_liquido: 55.0 },
  { name: "Premium", price: 199.90, lucro_liquido: 90.0 },
  { name: "Empresarial", price: 399.90, lucro_liquido: 180.0 },
];
export const DEFAULT_BONUS_PCT = 0.05;

export const DEFAULT_WHATSAPP_TEMPLATE =
  "Olá {primeiroNome}! Aqui é {vendedor} da FLUA. Estamos retomando contatos que ficaram em standby{motivoParenteses}. Podemos conversar sobre uma proposta agora?";

export const DEFAULT_MOTIVOS_PADRAO = [
  "Fora da área de cobertura",
  "Sem infraestrutura de fibra",
  "Aguardando expansão da rede",
  "Orçamento apertado",
  "Contrato ativo com concorrente",
  "Aguardando assembleia de condomínio",
  "Fora do timing comercial",
  "Regras internas não permitem",
];

export const renderWhatsAppTemplate = (tpl, p = {}) => {
  const primeiroNome = String(p.nome || "").split(" ")[0] || "";
  const motivo = String(p.motivo || "").trim();
  const motivoParenteses = motivo ? ` (na época o motivo foi: ${motivo.toLowerCase()})` : "";
  return String(tpl || DEFAULT_WHATSAPP_TEMPLATE)
    .replaceAll("{nome}", p.nome || "")
    .replaceAll("{primeiroNome}", primeiroNome)
    .replaceAll("{vendedor}", p.vendedor || "da FLUA")
    .replaceAll("{motivo}", motivo || "—")
    .replaceAll("{motivoParenteses}", motivoParenteses)
    .replaceAll("{localidade}", p.localidade || "");
};

export const getPlanNames = (plans = DEFAULT_PLANS) => plans.map((p) => p.name);
export const findPlan = (name, plans = DEFAULT_PLANS) => plans.find((p) => p.name === name);
export const planPrice = (name, plans = DEFAULT_PLANS) => Number(findPlan(name, plans)?.price || 0);
export const planLucroLiquido = (name, plans = DEFAULT_PLANS) =>
  Number(findPlan(name, plans)?.lucro_liquido || 0);

export const filterByMonth = (clientes = [], month = "") => {
  if (!month) return clientes;
  return clientes.filter((c) => (c.created_at || "").startsWith(month));
};

/**
 * Agrupa clientes por vendedor com métricas ponderadas pelos planos e pela
 * comissão personalizada do vendedor.
 * @param {Array} clientes
 * @param {Array} plans
 * @param {Object} commissionMap { [vendedor_name]: commission_pct 0..1 }
 */
export const computeVendorMetrics = (clientes = [], plans = DEFAULT_PLANS, commissionMap = {}) => {
  const map = new Map();
  for (const c of clientes) {
    const nome = (c.vendedor || "").trim() || "Sem vendedor";
    const price = planPrice(c.plano_escolhido, plans);
    const lucroUnit = planLucroLiquido(c.plano_escolhido, plans);
    const comissaoPct = Number(commissionMap[nome] || 0);
    const commission = price * comissaoPct;
    const acc = map.get(nome) || {
      nome,
      vendas: 0,
      receita: 0,
      lucro_bruto: 0,
      comissao_pct: comissaoPct,
      comissao: 0,
      planos: {},
      clientes: [],
    };
    acc.vendas += 1;
    acc.receita += price;
    acc.lucro_bruto += lucroUnit;
    acc.comissao += commission;
    acc.comissao_pct = comissaoPct; // garante que fica atual
    acc.planos[c.plano_escolhido || "—"] = (acc.planos[c.plano_escolhido || "—"] || 0) + 1;
    acc.clientes.push({
      ...c,
      _price: price,
      _lucro: lucroUnit,
      _commission: commission,
    });
    map.set(nome, acc);
  }
  return [...map.values()].map((v) => ({
    ...v,
    ticket_medio: v.vendas > 0 ? v.receita / v.vendas : 0,
    lucro_liquido_final: v.lucro_bruto - v.comissao,
  }));
};

export const withBonusAndMeta = (
  metrics = [],
  metasMap = {},
  bonusPct = DEFAULT_BONUS_PCT,
  teamHit = false,
  teamBonusPct = 0,
  bonusPctPerVendedor = {} // { [nome]: 0..1 } — sobrescreve bonusPct global se presente
) => {
  return metrics.map((v) => {
    const meta = Number(metasMap[v.nome] || 0);
    const atingido = meta > 0 ? (v.receita / meta) * 100 : 0;
    const bateu_meta = meta > 0 && v.receita >= meta;
    // Bônus efetivo: se vendedor tem override, usa. Senão herda o global.
    const effectiveBonus = bonusPctPerVendedor[v.nome] !== undefined && bonusPctPerVendedor[v.nome] !== null
      ? Number(bonusPctPerVendedor[v.nome])
      : bonusPct;
    const bonus = bateu_meta ? v.comissao * effectiveBonus : 0;
    const bonus_team = teamHit ? v.comissao * teamBonusPct : 0;
    const comissao_total = v.comissao + bonus + bonus_team;
    return {
      ...v,
      meta,
      atingido,
      bateu_meta,
      bonus,
      bonus_team,
      bonus_pct_effective: effectiveBonus,
      comissao_total,
      // Lucro final considerando tudo que sai da empresa (comissão base + bônus)
      lucro_liquido_final: v.lucro_bruto - comissao_total,
    };
  });
};
